import React from 'react';
import { formatMoney } from '../owners/formatMoney';

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

const Figure = ({ label, children }) => (
    <span className="inline-flex items-baseline gap-1.5 whitespace-nowrap">
        <span className="text-gray-500">{label}</span>
        {children}
    </span>
);

const ExpiryCounts = ({ late, lateLabel, soon }) => {
    if (!late && !soon) return <span className="font-medium text-green-700">None due</span>;
    return (
        <span className="inline-flex items-baseline gap-1.5">
            {late > 0 && <span className="font-semibold text-red-600">{late} {lateLabel}</span>}
            {late > 0 && soon > 0 && <span className="text-gray-400">/</span>}
            {soon > 0 && <span className="font-semibold text-amber-700">{soon} due in 30 days</span>}
        </span>
    );
};

/**
 * One-line totals for the cars in view; follows the active tab, search and filters.
 * @param {Object} props
 * @param {Object} props.summary - Result of summarizeCars()
 * @param {boolean} props.showBalance - False on the Private tab, where weekly balances don't apply
 * @param {string|null} props.coverageWeek - Week the coverage is measured against
 */
const CarsSummaryLine = ({ summary, showBalance, coverageWeek }) => (
    <div
        className="mb-3 flex flex-wrap items-baseline gap-x-5 gap-y-1.5 border-y border-gray-100 py-2 text-xs text-[#2a2e30]"
        aria-label="Summary of the cars in view"
    >
        <Figure label="Vehicles">
            <span className="font-semibold tabular-nums">{summary.vehicleCount.toLocaleString('en-US')}</span>
        </Figure>
        {showBalance && (
            <Figure label="Outstanding">
                <span className={`font-semibold tabular-nums ${summary.outstanding.total > 0 ? 'text-red-600' : 'text-green-700'}`}>
                    {formatMoney(summary.outstanding.total)}
                </span>
                {summary.outstanding.count > 0 && (
                    <span className="text-gray-500">on {plural(summary.outstanding.count, 'car')}</span>
                )}
            </Figure>
        )}
        {showBalance && summary.coverage && (
            <span
                className="inline-flex items-baseline gap-1.5 whitespace-nowrap"
                title={`Net balance of these cars (${formatMoney(summary.coverage.net)}) as a share of their invoices for ${coverageWeek || 'this week'} (${formatMoney(summary.coverage.invoiced)}). Same measure as Weekly Coverage on the home page.`}
            >
                <span className="text-gray-500">Weekly coverage</span>
                <span className="font-semibold tabular-nums text-brand-primary">
                    {summary.coverage.pct === null ? '—' : `${summary.coverage.pct.toFixed(1)}%`}
                </span>
            </span>
        )}
        <Figure label="Insurance">
            <ExpiryCounts late={summary.insurance.expired} lateLabel="expired" soon={summary.insurance.expiring} />
        </Figure>
        <Figure label="Inspection">
            <ExpiryCounts late={summary.inspection.overdue} lateLabel="overdue" soon={summary.inspection.dueSoon} />
        </Figure>
    </div>
);

export default CarsSummaryLine;
